/**
 * StepCard Component
 *
 * Renders a single step in the First Sale Tracker with three visual states:
 *   - Locked: prior step incomplete, muted and non-interactive
 *   - Active: next step to complete, highlighted with call-to-action
 *   - Completed: green check, success accent, shows completion timestamp
 *
 * Contains a completion toggle (accessible native checkbox), collapsible
 * notes textarea with save-on-blur, and a resource links area.
 */

import React, { useState, useCallback, forwardRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { MDBCard, MDBCardBody, MDBIcon, MDBBtn, MDBCollapse, MDBTooltip } from 'mdb-react-ui-kit'
import AgencyContentStore from '../../../../shared/stores/agencyContent.store'
import AdminContentEditPopupComponent from '../../../../components/adminShared/AdminContentEditPopupComponent'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import Store from '../../FirstSaleTracker.store'
import { TOTAL_STEPS, NOTES_MAX_LENGTH } from '../../FirstSaleTracker.constants'

import './StepCard.scss'

const StepCard = forwardRef(({ step, config, stepNumber, isLocked, isActive, isCompleted, onScrollToNext }, ref) => {
  const [notesOpen, setNotesOpen] = useState(false)
  const [localNotes, setLocalNotes] = useState(step.notes || '')
  const [showCascadeWarning, setShowCascadeWarning] = useState(false)

  const saveStatus = Store.saveStatus[stepNumber]

  /**
   * Handle completion toggle click.
   * If unchecking and later steps are completed, show cascade warning first.
   */
  const saving = Store.saving
  const handleToggle = useCallback(() => {
    if (isLocked || saving) return

    if (isCompleted) {
      // Check if any later steps are completed
      const hasLaterCompleted = Store.steps.some(
        s => parseInt(s.step_number) > stepNumber && parseInt(s.completed) === 1
      )
      if (hasLaterCompleted) {
        setShowCascadeWarning(true)
        return
      }
    }

    Store.toggleStep(stepNumber, !isCompleted)

    // Scroll to next step on mobile after completing
    if (!isCompleted && stepNumber < TOTAL_STEPS) {
      onScrollToNext(stepNumber + 1)
    }
  }, [isLocked, isCompleted, stepNumber, onScrollToNext, saving])

  /** Confirm cascade uncomplete. */
  const confirmCascade = useCallback(() => {
    setShowCascadeWarning(false)
    Store.toggleStep(stepNumber, false)
  }, [stepNumber])

  /** Cancel cascade uncomplete. */
  const cancelCascade = useCallback(() => {
    setShowCascadeWarning(false)
  }, [])

  /** Handle notes textarea change. Track dirty state. */
  const handleNotesChange = useCallback(
    e => {
      const value = e.target.value
      if (value.length <= NOTES_MAX_LENGTH) {
        setLocalNotes(value)
        Store.setDirtyNote(stepNumber, value)
      }
    },
    [stepNumber]
  )

  /** Save notes on blur. */
  const handleNotesBlur = useCallback(() => {
    if (localNotes !== (step.notes || '')) {
      Store.updateStepNotes(stepNumber, localNotes)
    } else {
      // No change, just clear dirty state
      Store.clearDirtyNote(stepNumber)
    }
  }, [localNotes, step.notes, stepNumber])

  // Sync local notes when step data changes from server
  React.useEffect(() => {
    if (!Store.dirtyNotes[stepNumber]) {
      setLocalNotes(step.notes || '')
    }
  }, [step.notes, stepNumber])

  const charsRemaining = NOTES_MAX_LENGTH - localNotes.length
  const showCharCounter = charsRemaining <= 50

  // Dynamic description from agency_content (admin-editable)
  const agencyContent = AgencyContentStore.content[config.contentSlug]
  const dynamicDescription = agencyContent?.content || null

  const cardClass = [
    'step-card',
    isLocked ? 'step-card--locked' : '',
    isActive ? 'step-card--active' : '',
    isCompleted ? 'step-card--completed' : '',
  ]
    .filter(Boolean)
    .join(' ')

  // Auto-detect prompt: only for Step 8, only when active, only when a sale was found
  const detectedSale = Store.detectedSale
  const showSaleDetectedPrompt = stepNumber === TOTAL_STEPS && isActive && !isCompleted && !!detectedSale
  const formattedSaleDate = showSaleDetectedPrompt
    ? new Date(detectedSale.av_date + 'T12:00:00').toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const checkboxId = `step-${stepNumber}-complete`

  return (
    <div ref={ref} className={cardClass}>
      <MDBCard>
        <MDBCardBody className='d-flex flex-column'>
          {/* Step header row */}
          <div className='step-card__header'>
            {/* Step number badge */}
            <div className='step-card__number'>
              {isCompleted ? (
                <MDBIcon icon='check' />
              ) : isLocked ? (
                <MDBTooltip tag='span' title='Complete the previous step to unlock'>
                  <MDBIcon icon='lock' />
                </MDBTooltip>
              ) : (
                stepNumber
              )}
            </div>

            {/* Step title and description */}
            <div className='step-card__content'>
              <h6 className='step-card__title'>{agencyContent?.page_name || config.label || step.step_key}</h6>
              {dynamicDescription ? (
                <div className='step-card__description' dangerouslySetInnerHTML={{ __html: dynamicDescription }} />
              ) : (
                <p className='step-card__description'>{config.description || ''}</p>
              )}
              {agencyContent?.id && (
                <AdminContentEditPopupComponent
                  type='RTE'
                  pageId={agencyContent.id}
                  readonlyTitle={!UserProfileService.isA(['system-admin'], true)}
                  color='blue'
                  iconPosition='top-right'
                  iconStyle={{ transform: 'translate(-3px, 3px)' }}
                  callback={id => AgencyContentStore.fetchByContentObj({ contentId: id })}
                />
              )}
              {isCompleted && step.completed_at && (
                <small className='step-card__timestamp text-muted'>
                  Completed{' '}
                  {new Date(step.completed_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </small>
              )}
            </div>

            {/* Completion toggle — native checkbox for accessibility */}
            <div className='step-card__toggle'>
              <input
                type='checkbox'
                id={checkboxId}
                checked={isCompleted}
                onChange={handleToggle}
                disabled={isLocked || Store.saving}
                className='step-card__checkbox'
                aria-label={`Mark "${config.label || step.step_key}" as complete`}
              />
              <label htmlFor={checkboxId} className='step-card__checkbox-label'>
                <MDBIcon far={!isCompleted} icon={isCompleted ? 'check-circle' : 'circle'} />
              </label>
            </div>
          </div>

          {/* Cascade uncomplete warning */}
          {showCascadeWarning && (
            <div className='step-card__cascade-warning'>
              <p>
                Unchecking this step will also reset steps {stepNumber + 1} through {TOTAL_STEPS}. Your notes will be
                preserved.
              </p>
              <div>
                <MDBBtn type='button' size='sm' color='danger' onClick={confirmCascade}>
                  Reset Steps
                </MDBBtn>
                <MDBBtn type='button' size='sm' color='flat' onClick={cancelCascade}>
                  Cancel
                </MDBBtn>
              </div>
            </div>
          )}

          {/* Sale detected prompt — Step 8 only */}
          {showSaleDetectedPrompt && (
            <div className='step-card__sale-detected'>
              <MDBIcon icon='check-circle' className='step-card__sale-detected-icon' />
              <div className='step-card__sale-detected-body'>
                <p className='step-card__sale-detected-text'>
                  We found a sale submitted on <strong>{formattedSaleDate}</strong>. Mark this step complete?
                </p>
                <MDBBtn
                  type='button'
                  color='success'
                  size='sm'
                  onClick={() => Store.toggleStep(stepNumber, true)}
                  disabled={Store.saving}
                >
                  <MDBIcon icon='check' className='mr-1' />
                  Yes, Mark Complete
                </MDBBtn>
              </div>
            </div>
          )}

          {/* Notes toggle and resource link */}
          {!isLocked && (
            <div className='step-card__actions'>
              <MDBBtn
                type='button'
                color='flat'
                size='sm'
                className='step-card__notes-toggle p-1'
                onClick={() => setNotesOpen(!notesOpen)}
              >
                <MDBIcon icon='sticky-note' className='mr-1' />
                {notesOpen ? 'Hide Notes' : 'Notes'}
                {localNotes && !notesOpen && <MDBIcon icon='circle' className='ml-1 text-primary note-indicator' />}
              </MDBBtn>

              {/* Internal link for Step 8 (Submit Sales) */}
              {config.internalLink && (
                <Link to={config.internalLink} className='step-card__resource-link'>
                  <MDBBtn type='button' outline color='primary' size='sm' className='p-1 px-2'>
                    <MDBIcon icon='external-link-alt' className='mr-1' />
                    Go to Sales Submission
                  </MDBBtn>
                </Link>
              )}
            </div>
          )}

          {/* Collapsible notes textarea */}
          <MDBCollapse open={notesOpen && !isLocked}>
            <div className='step-card__notes mt-2'>
              <textarea
                className='form-control step-card__textarea'
                rows='3'
                placeholder='Jot quick notes — prospect name, call details, reminders...'
                value={localNotes}
                onChange={handleNotesChange}
                onBlur={handleNotesBlur}
                maxLength={NOTES_MAX_LENGTH}
                disabled={Store.saving}
              />
              <div className='step-card__notes-footer'>
                {/* Save status indicator */}
                {saveStatus === 'saved' && (
                  <span className='step-card__save-indicator step-card__save-indicator--saved'>
                    <MDBIcon icon='check' className='mr-1' />
                    Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span className='step-card__save-indicator step-card__save-indicator--error'>
                    <MDBIcon icon='exclamation-circle' className='mr-1' />
                    Save failed —{' '}
                    <button className='btn-link p-0' onClick={() => Store.updateStepNotes(stepNumber, localNotes)}>
                      retry
                    </button>
                  </span>
                )}
                {/* Character counter near limit */}
                {showCharCounter && (
                  <span className={`step-card__char-count ${charsRemaining <= 0 ? 'text-danger' : 'text-warning'}`}>
                    {localNotes.length} / {NOTES_MAX_LENGTH}
                  </span>
                )}
              </div>
            </div>
          </MDBCollapse>
        </MDBCardBody>
      </MDBCard>
    </div>
  )
})

StepCard.displayName = 'StepCard'

export default observer(StepCard)
